import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import {
  emailApi,
  type ComposeInput,
  type CreateEmailAccountInput,
  type EmailBulkAction,
  type EmailMessageDetail,
  type EmailMessageListItem,
  type UpdateEmailAccountInput,
} from '@/lib/email-api'

/**
 * Virtuálny pohľad „S hviezdičkou" — správy s hviezdičkou naprieč všetkými
 * priečinkami účtu. Nemá reálny priečinok (rovnako ako v mobile), list ide s
 * `flagged=true` bez `folder`.
 */
export const STARRED_VIEW = 'STARRED'

/**
 * Typed React Query layer for the `/email/*` endpoints.
 *
 * Unlike the other domains, the webmail already had a centralised transport
 * wrapper (`emailApi` in `@/lib/email-api`) owning URLs + envelopes + types.
 * This module adds the missing React Query layer on top of it: a single query-key
 * factory plus typed `useQuery`/`useMutation` hooks that wrap `emailApi`. The
 * mutation hooks own cache invalidation; pages keep toasts / drawer-close / nav /
 * form-state at the call site via `mutate(vars, { onSuccess, onError })`.
 *
 * The keys are reproduced VERBATIM from the previous ad-hoc inline usage so the
 * many partial-key `invalidateQueries` (and the Sidebar "Emaily" unread badge,
 * which shares `['email-unread-counts']`) keep matching:
 *   - accounts      → ['email-accounts']
 *   - unread counts → ['email-unread-counts']
 *   - message lists → ['email-messages', accountId, folderRole, searchQuery, page]
 *                     (broad prefix ['email-messages'] for invalidation)
 *   - message detail→ ['email-message', id]
 *   - folders       → ['email-folders', …]
 *   - recipients    → ['email-recipients', q]
 *   - comm history  → ['comm-emails', { contactId, dealId }]
 */
export const emailKeys = {
  /** Accounts list (also the composer's signature/own-address source). */
  accounts: ['email-accounts'] as const,
  /** Unread INBOX counts — shared with the sidebar badge. */
  unreadCounts: ['email-unread-counts'] as const,
  /** Broad prefix matching every message-list page — use for invalidation. */
  messages: ['email-messages'] as const,
  /** Per-account message-list prefix (sync invalidation). */
  messagesByAccount: (accountId: string) => ['email-messages', accountId] as const,
  /** A concrete message-list page. */
  messagesList: (accountId: string, folderRole: string, searchQuery: string, page: number, unreadOnly = false) =>
    ['email-messages', accountId, folderRole, searchQuery, page, unreadOnly] as const,
  /** A single message (reader pane). */
  message: (id: string) => ['email-message', id] as const,
  /** Folder list — broad prefix ['email-folders'] used for invalidation. */
  folders: (accountId: string) => ['email-folders', accountId] as const,
  /** Composer recipient autocomplete. */
  recipients: (q: string) => ['email-recipients', q] as const,
  /** CRM comm-history rows for a contact/deal. */
  commHistory: (params: { contactId?: string; dealId?: number }) =>
    ['comm-emails', params] as const,
}

// ─── Queries ────────────────────────────────────────────────────────────────

/** Connected mailboxes for the current user. */
export function useEmailAccounts() {
  return useQuery({
    queryKey: emailKeys.accounts,
    queryFn: () => emailApi.accounts.list(),
  })
}

/** Total + per-account unread INBOX counts (switcher pills + sidebar badge). */
export function useEmailUnreadCounts() {
  return useQuery({
    queryKey: emailKeys.unreadCounts,
    queryFn: () => emailApi.unreadCounts(),
  })
}

/** Paginated message list for the active account/folder/search. */
export function useEmailMessages(params: {
  accountId: string
  folderRole: string
  searchQuery: string
  page: number
  /** Len neprečítané. */
  unreadOnly?: boolean
}) {
  const { accountId, folderRole, searchQuery, page, unreadOnly = false } = params
  return useQuery({
    queryKey: emailKeys.messagesList(accountId, folderRole, searchQuery, page, unreadOnly),
    queryFn: () =>
      emailApi.messages.list({
        accountId,
        ...(folderRole === STARRED_VIEW ? { flagged: true } : { folder: folderRole }),
        q: searchQuery || undefined,
        page,
        ...(unreadOnly ? { unread: true } : {}),
      }),
    placeholderData: keepPreviousData,
    enabled: !!accountId,
  })
}

/** Full message for the reader pane. Side-effect free — read-marking is an explicit POST /read. */
export function useEmailMessage(messageId: string | undefined) {
  return useQuery({
    queryKey: emailKeys.message(messageId ?? ''),
    queryFn: () => emailApi.messages.get(messageId!),
    enabled: !!messageId,
  })
}

/** Folder list (with unread counts) for an account. */
export function useEmailFolders(accountId: string | undefined) {
  return useQuery({
    queryKey: emailKeys.folders(accountId ?? ''),
    queryFn: () => emailApi.folders(accountId!),
    enabled: !!accountId,
  })
}

/** Composer recipient autocomplete. Caller gates via `enabled`. */
export function useEmailRecipients(q: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: emailKeys.recipients(q),
    queryFn: () => emailApi.searchRecipients(q),
    enabled: options?.enabled,
  })
}

/** CRM comm-history metadata for a contact/deal. */
export function useEmailCommHistoryQuery(params: {
  contactId?: string
  dealId?: number
}) {
  const { contactId, dealId } = params
  const enabled = !!contactId || dealId != null
  return useQuery({
    queryKey: emailKeys.commHistory({ contactId, dealId }),
    queryFn: () => emailApi.commHistory({ contactId, dealId }),
    enabled,
    retry: false,
  })
}

// ─── Message mutations ────────────────────────────────────────────────────────
// Each hook owns its invalidation set, preserved verbatim from the previous
// inline mutations. The message-state actions (unread/read/trash/spam) refresh
// the list + folder + unread badges; bulk refreshes list + unread (no folders);
// send/draft refresh list + folders (no unread).

/** Refresh list + folders + unread badges (reader message-state actions). */
function useInvalidateMessageView() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: emailKeys.messages })
    qc.invalidateQueries({ queryKey: ['email-folders'] })
    qc.invalidateQueries({ queryKey: emailKeys.unreadCounts })
  }
}

/** Mark a message read. Invalidates list + folders + unread. */
export function useMarkEmailRead(messageId: string | undefined) {
  const invalidate = useInvalidateMessageView()
  return useMutation({
    mutationFn: () => emailApi.messages.markRead(messageId!),
    onSuccess: invalidate,
  })
}

/** Mark a message unread. Invalidates list + folders + unread. */
export function useMarkEmailUnread(messageId: string | undefined) {
  const invalidate = useInvalidateMessageView()
  return useMutation({
    mutationFn: () => emailApi.messages.markUnread(messageId!),
    onSuccess: invalidate,
  })
}

type MessageListPage = { data: EmailMessageListItem[]; meta: { total: number; take: number } }

/** Prepíše správu vo všetkých načítaných stránkach zoznamov (aj vo virtuálnom STARRED). */
function patchMessageInLists(qc: QueryClient, id: string, patch: Partial<EmailMessageListItem>) {
  qc.setQueriesData<MessageListPage>({ queryKey: emailKeys.messages }, (old) => {
    if (!old?.data?.some((m) => m.id === id)) return old
    return { ...old, data: old.data.map((m) => (m.id === id ? { ...m, ...patch } : m)) }
  })
}

/**
 * Hviezdička (flag / unflag). Optimisticky: ikona sa preklopí hneď v zoznamoch
 * aj v detaile, server potvrdzuje na pozadí; pri chybe rollback zo snapshotu
 * (rovnako ako v mobile — bez toho druhý rýchly klik počítal zo stale hodnoty).
 */
export function useFlagEmail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, isFlagged }: { id: string; isFlagged: boolean }) => emailApi.messages.flag(id, isFlagged),
    onMutate: async ({ id, isFlagged }) => {
      await qc.cancelQueries({ queryKey: emailKeys.messages })
      await qc.cancelQueries({ queryKey: emailKeys.message(id) })
      const prevLists = qc.getQueriesData<MessageListPage>({ queryKey: emailKeys.messages })
      const prevDetail = qc.getQueryData<EmailMessageDetail>(emailKeys.message(id))
      patchMessageInLists(qc, id, { isFlagged })
      if (prevDetail) qc.setQueryData<EmailMessageDetail>(emailKeys.message(id), { ...prevDetail, isFlagged })
      return { prevLists, prevDetail }
    },
    onError: (_e, { id }, ctx) => {
      ctx?.prevLists.forEach(([key, data]) => qc.setQueryData(key, data))
      if (ctx?.prevDetail) qc.setQueryData(emailKeys.message(id), ctx.prevDetail)
    },
    onSettled: (_d, _e, { id }) => {
      qc.invalidateQueries({ queryKey: emailKeys.messages })
      qc.invalidateQueries({ queryKey: emailKeys.message(id) })
    },
  })
}

/** Move a message to Trash. Invalidates list + folders + unread. */
export function useTrashEmail(messageId: string | undefined) {
  const invalidate = useInvalidateMessageView()
  return useMutation({
    mutationFn: () => emailApi.messages.trash(messageId!),
    onSuccess: invalidate,
  })
}

/** Move a message to Spam. Invalidates list + folders + unread. */
export function useSpamEmail(messageId: string | undefined) {
  const invalidate = useInvalidateMessageView()
  return useMutation({
    mutationFn: () => emailApi.messages.spam(messageId!),
    onSuccess: invalidate,
  })
}

/** Move a message to Archive. Invalidates list + folders + unread. */
export function useArchiveEmail(messageId: string | undefined) {
  const invalidate = useInvalidateMessageView()
  return useMutation({
    mutationFn: () => emailApi.messages.archive(messageId!),
    onSuccess: invalidate,
  })
}

/**
 * Bulk read / unread / trash / move for the list multi-select (move = presun do
 * priečinka `folderId`: Archív, Spam, návrat do Doručených). Invalidates list +
 * unread; move navyše priečinky (menia sa im počty).
 */
export function useBulkEmailAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, action, folderId }: { ids: string[]; action: EmailBulkAction; folderId?: string }) =>
      emailApi.messages.bulk(ids, action, folderId),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: emailKeys.messages })
      qc.invalidateQueries({ queryKey: emailKeys.unreadCounts })
      if (vars.action === 'move') qc.invalidateQueries({ queryKey: ['email-folders'] })
    },
  })
}

// ─── Compose / drafts / schedule ──────────────────────────────────────────────

/** Refresh list + folders (a sent/drafted message appears in Sent/Drafts). */
function useInvalidateAfterCompose() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: emailKeys.messages })
    qc.invalidateQueries({ queryKey: ['email-folders'] })
  }
}

/** Send (or schedule) a message. Invalidates list + folders. */
export function useSendEmail() {
  const invalidate = useInvalidateAfterCompose()
  return useMutation({
    mutationFn: (payload: ComposeInput) => emailApi.send(payload),
    onSuccess: invalidate,
  })
}

/** Save a draft. Invalidates list + folders. */
export function useCreateEmailDraft() {
  const invalidate = useInvalidateAfterCompose()
  return useMutation({
    mutationFn: (payload: ComposeInput) => emailApi.drafts.create(payload),
    onSuccess: invalidate,
  })
}

/** Update an existing draft. Invalidates list + folders. */
export function useUpdateEmailDraft(id: string) {
  const invalidate = useInvalidateAfterCompose()
  return useMutation({
    mutationFn: (payload: ComposeInput) => emailApi.drafts.update(id, payload),
    onSuccess: invalidate,
  })
}

/** Send a saved draft. Invalidates list + folders. */
export function useSendEmailDraft() {
  const invalidate = useInvalidateAfterCompose()
  return useMutation({
    mutationFn: (id: string) => emailApi.drafts.send(id),
    onSuccess: invalidate,
  })
}

/** Cancel a scheduled send. Invalidates list + folders. */
export function useCancelEmailSchedule() {
  const invalidate = useInvalidateAfterCompose()
  return useMutation({
    mutationFn: (id: string) => emailApi.cancelSchedule(id),
    onSuccess: invalidate,
  })
}

/** Stage an attachment upload. No cache invalidation — caller stores the result. */
export function useUploadEmailAttachment() {
  return useMutation({
    mutationFn: (file: File) => emailApi.uploadAttachment(file),
  })
}

// ─── Account CRUD ──────────────────────────────────────────────────────────────

/** Connect (create + verify) a mailbox. Invalidates the accounts list. */
export function useCreateEmailAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateEmailAccountInput) => emailApi.accounts.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: emailKeys.accounts }),
  })
}

/** Patch mailbox settings. Invalidates the accounts list. */
export function useUpdateEmailAccount(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateEmailAccountInput) => emailApi.accounts.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: emailKeys.accounts }),
  })
}

/** Disconnect (soft-delete) a mailbox. Invalidates the accounts list. The
 * EmailsPage switcher additionally invalidates unread counts at the call site. */
export function useDeleteEmailAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => emailApi.accounts.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: emailKeys.accounts }),
  })
}

/** Test a mailbox's IMAP/SMTP connection. Invalidates accounts (status changes). */
export function useTestEmailAccount(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => emailApi.accounts.test(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: emailKeys.accounts }),
  })
}

/** Queue a mailbox sync. Invalidates that account's lists + unread counts. */
export function useSyncEmailAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (accountId: string) => emailApi.accounts.sync(accountId),
    onSuccess: (_res, accountId) => {
      qc.invalidateQueries({ queryKey: emailKeys.messagesByAccount(accountId) })
      qc.invalidateQueries({ queryKey: emailKeys.unreadCounts })
    },
  })
}

/** Save a mailbox signature. Invalidates the accounts list. */
export function useUpdateEmailSignature(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (signatureHtml: string) => emailApi.accounts.updateSignature(id, signatureHtml),
    onSuccess: () => qc.invalidateQueries({ queryKey: emailKeys.accounts }),
  })
}
